import React, { useContext, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import { UserContext } from "@context/UserContext";
import { BrandContext } from "@context/BrandContext";
import { ContentContext } from "@context/ContentContext";
import axios from "@common/axios";
import { PageMarkerNavigation } from "./PageMarkerNavigation";
import CircularLoader from "@common/CircularLoader";
import ResourceNavigationDevelopment from "./ResourceNavigationDevelopment";
import CMSResourceNavigation from "./CMSResourceNavigation";
import { TemplateContext } from "@common/context/TemplateContext";
import Button from "@common/Button";
import { CrossIcon, ChevronRightIcon, ChevronLeftIcon } from "@common/icons";
import { useScreenMode } from "@common/context/ScreenModeContext";

const NavigationRightDevelopment = props => {
    const {compressed, showResources = true, callback, children, closeNav} = props;
    const {user, updateRightMenuClosed} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const { content } = useContext(ContentContext);
    const [resourceGroups, setResourceGroups] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const { shortcutLocation, navigationLinks } = useContext(TemplateContext);
    const closeButtonRef = useRef(null);
    const { smallScreenMode } = useScreenMode();
    const [isMinimized, setIsMinimized] = useState(false);

    const loadResources = (conceptId) => {
        setLoading(true);
        axios.get('/web/ui/content/concept/resources', { params: { conceptId: conceptId } })
            .then(response => {
                setResourceGroups(response.data);
            }).catch(e => {
            console.error('Error loading resources', e);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (user.publicSession || user.expired) {
            return;
        }

        if (!compressed && smallScreenMode) {
            updateRightMenuClosed(true);
        } else if (brandConfig.rightMenuOpen) {
            updateRightMenuClosed(false);
        }
    }, [smallScreenMode]);

    useEffect(() => {
        if (content.currentConcept !== undefined) {
            loadResources(content.currentConcept.conceptId);
        } else {
            setLoading(false);
        }
    }, [content]);

    const toggleMinimized = () => {
        const newState = !isMinimized;
        setIsMinimized(newState);
        if (updateRightMenuClosed) {
            updateRightMenuClosed(newState);
        }
    };

    useEffect(() => {
        // Set focus when tabbing from small screen button
        // Using a timeout to give the component time to render
        if (smallScreenMode) {
            const timer = setTimeout(() => {
                if (closeButtonRef.current) {
                    closeButtonRef.current.focus();
                }
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [smallScreenMode]);

    if ((user.publicSession && !user.guideMode) || user.expired) {
        return null;
    }

    if (isLoading) {
        return <div className={'ch-nav-lesson ch-nav-lesson-loader'}><CircularLoader pageCentered={true} displayInElement={true}/></div>;
    }

    const hasResources = resourceGroups.some(resource => resource.resources);
    const hasLinks = navigationLinks && navigationLinks.length > 0;
    const hasContent = children || hasResources || hasLinks;

    if (!hasContent) {
        return smallScreenMode ? (
            <div className={'ch-nav-lesson'}>
                <div className={'ch-nav-lesson-title-wrapper'}>
                    <Button type={'outline'} ref={closeButtonRef} onClick={() => closeNav()} aria-label={'Close'}><CrossIcon /></Button>
                </div>
                <p>No shortcuts or resources for this page.</p>
            </div>
        ) : null;
    }

    return (
        <div className={`ch-nav-lesson ${isMinimized ? 'ch-nav-lesson-minimized' : ''}`}>
            <div className="ch-nav-r-inner ch-nav-inner-compact">
                <div className='ch-nav-lesson-title-wrapper'>
                    {(!isMinimized && !user.cmsMode && shortcutLocation === 'LESSON') &&
                        <h2 className="ch-nav-lesson-title">In this {brandConfig.conceptName?.toLowerCase()}</h2>
                    }
                    {!smallScreenMode && shortcutLocation === 'LESSON' && (
                        isMinimized ? (
                            <Tooltip title="Expand Navigation" placement="top">
                                <Button
                                    type="text"
                                    onClick={toggleMinimized}
                                    aria-label="Expand Navigation"
                                >
                                    <ChevronLeftIcon />
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                type="text"
                                onClick={toggleMinimized}
                                aria-label="Minimize Navigation"
                            >
                                <ChevronRightIcon />
                            </Button>
                        )
                    )}
                    {smallScreenMode && (
                        <Button type={'outline'} ref={closeButtonRef} onClick={() => closeNav()} aria-label={'Close'}>
                            <CrossIcon/>
                        </Button>
                    )}
                </div>
                {!isMinimized && (
                    <div className="ch-nav-shortcuts-body">
                        {!children ? (
                            <nav aria-label={"Resource Navigation"} className={'ch-page-navigation ch-activity-navigation'} role={''}>
                                <PageMarkerNavigation indicatorPlacement="right" startOpen={user.cmsMode}/>
                                {showResources &&
                                    <div className={'ch-list'}>
                                        {user.cmsMode &&
                                            <CMSResourceNavigation shortcutLocation={shortcutLocation} />
                                        }
                                        {(!user.cmsMode && shortcutLocation === 'LESSON') &&
                                            <ResourceNavigationDevelopment key={shortcut.shortcutId} shortcut={shortcut} indicatorPlacement="right" resourceGroups={resourceGroups}/>
                                        }
                                    </div>
                                }
                            </nav>
                        ) : (
                            <nav>
                                {children}
                            </nav>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default NavigationRightDevelopment;